import { PlusIcon } from '@heroicons/react/20/solid';

import { InviteMenu } from './InviteMenu';

type NoSeatsEmptyStateProps = {
  remaining: number;
  translations?: Record<string, string>;
  onInviteByEmail?: () => void;
  onBulkInviteCsv?: () => void;
};

// Translation keys deliberately mirror the orchid-v2 i18next path so the host
// app can supply localized copy (and singular/plural variants) without the
// component reaching into i18n itself.
const HEADING_KEY_ONE = 'group_subscriptions.no_seats.heading_one';
const HEADING_KEY_OTHER = 'group_subscriptions.no_seats.heading_other';
const DESCRIPTION_KEY = 'group_subscriptions.no_seats.description';
const CTA_KEY = 'group_subscriptions.no_seats.cta';

const FALLBACK_COPY = {
  headingOne: '{{count}} seat available',
  headingOther: '{{count}} seats available',
  description:
    'Invite members to start using this group subscription. Once invites are sent and accepted, members will appear here.',
  cta: 'Invite members',
} as const;

export function NoSeatsEmptyState({
  remaining,
  translations,
  onInviteByEmail,
  onBulkInviteCsv,
}: NoSeatsEmptyStateProps) {
  const headingKey = remaining === 1 ? HEADING_KEY_ONE : HEADING_KEY_OTHER;
  const headingTemplate =
    translations?.[headingKey] ?? (remaining === 1 ? FALLBACK_COPY.headingOne : FALLBACK_COPY.headingOther);
  const heading = headingTemplate.replace('{{count}}', String(remaining));
  const description = translations?.[DESCRIPTION_KEY] ?? FALLBACK_COPY.description;
  const cta = translations?.[CTA_KEY] ?? FALLBACK_COPY.cta;

  return (
    <div className="flex flex-col items-center justify-center gap-6 rounded-lg border border-gray-200 bg-white px-6 py-8 sm:px-20 sm:py-10">
      <div className="flex w-full flex-col items-center gap-2 text-center">
        <h4 className="max-w-[440px] text-lg font-medium leading-7 text-gray-900">{heading}</h4>
        <p className="max-w-[440px] text-sm leading-5 text-gray-500">{description}</p>
      </div>
      {(onInviteByEmail || onBulkInviteCsv) && (
        <InviteMenu
          triggerClassName="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-wt-primary px-3.5 py-2 text-sm font-medium text-wt-text-on-primary shadow-sm hover:opacity-90"
          onInviteByEmail={onInviteByEmail}
          onBulkInviteCsv={onBulkInviteCsv}
          align="center"
          translations={translations}
        >
          <PlusIcon className="h-4 w-4 shrink-0" />
          {cta}
        </InviteMenu>
      )}
    </div>
  );
}
